import { useState, type ReactNode } from "react"
import { Button, Card, Dropdown, Popover, Tag, Tooltip } from "antd"
import { Download, Info, MousePointerClick, MoreHorizontal, RefreshCw, Table2 } from "lucide-react"
import type { TileResult } from "@csbi/contracts"
import type { Downgrade } from "@csbi/viz-core"
import { WIDGET_LABELS } from "./labels.js"
import { TileStatePlaceholder, stateNote } from "./tile-state.js"

export type WidgetFrameProps = {
  title: string
  result?: TileResult
  downgrade?: Downgrade
  loading?: boolean
  error?: string
  onRefresh?: () => void
  refreshing?: boolean
  onExportCsv?: () => void
  onExportPng?: () => void
  /**
   * ⚠ **Shown in the title bar, not left to the cursor.** The first version of drilling signalled itself with a
   * pointer cursor and nothing else, and the honest report back was that a drillable chart and a dead one were
   * indistinguishable — correctly, because a cursor can only be read by comparison, and nobody hovers two cards
   * side by side to discover a feature. This mark is visible without hovering anything.
   */
  drillable?: boolean
  editActions?: ReactNode
  children: ReactNode
  tableView?: ReactNode
  height?: number | string
}

export function WidgetFrame({
  title,
  result,
  downgrade,
  loading = false,
  error,
  onRefresh,
  refreshing = false,
  onExportCsv,
  onExportPng,
  drillable = false,
  editActions,
  children,
  tableView,
  height = "100%",
}: WidgetFrameProps) {
  const [asTable, setAsTable] = useState(false)

  const approximate = result?.meta.metrics.filter((m) => m.approximate) ?? []
  const caveat = approximate.map((m) => `${m.name}: ${m.caveat ?? "This value is an approximation."}`).join("\n")
  const showPlaceholder = Boolean(result) && result!.state !== "ok"
  const partialProblems = result && result.state === "ok" ? result.perTenant.filter((t) => t.state !== "ok") : []

  const notes: { key: string; tag: ReactNode; detail: ReactNode; level: "note" | "warning" }[] = []
  if (approximate.length > 0)
    notes.push({
      key: "approx",
      level: "warning",
      tag: (
        <Tag color="warning" style={{ marginInlineEnd: 0 }}>
          approx.
        </Tag>
      ),
      detail: caveat,
    })
  if (result?.meta.truncated)
    notes.push({
      key: "trunc",
      level: "warning",
      tag: (
        <Tag color="error" style={{ marginInlineEnd: 0 }}>
          top {result.rows.length} only
        </Tag>
      ),
      detail:
        "The result hit the row cap and was truncated. The ranking is trustworthy, the totals are not — narrow the filters to see everything.",
    })
  if (downgrade)
    notes.push({
      key: "down",
      level: "note",
      tag: <Tag style={{ marginInlineEnd: 0 }}>shown as {WIDGET_LABELS[downgrade.to].toLowerCase()}</Tag>,
      detail: downgrade.reason,
    })
  if (partialProblems.length > 0 && result) {
    const note = stateNote({ state: result.state, perTenant: result.perTenant })
    if (note) notes.push({ key: "state", tag: note.tag, detail: note.detail, level: note.level })
  }

  const warned = notes.some((n) => n.level === "warning")

  return (
    <Card
      size="small"
      styles={{ body: { display: "flex", flexDirection: "column", height: "100%", padding: 0 } }}
      style={{ height, display: "flex", flexDirection: "column" }}
    >
      <div
        className="csbi-tile-handle"
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 6,
          padding: "8px 10px 8px 12px",
          borderBottom: "1px solid var(--csbi-gridline)",
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.35,
            color: "var(--csbi-ink-primary)",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            overflowWrap: "anywhere",
          }}
          title={title}
        >
          {title}
        </span>

        {drillable && (
          <Tooltip title="Click any bar to see it over time">
            <MousePointerClick
              size={13}
              aria-label="This chart can be drilled into"
              style={{ flex: "0 0 auto", marginLeft: 6, marginTop: 2, color: "var(--csbi-ink-muted)" }}
            />
          </Tooltip>
        )}

        <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 0 }}>
          {notes.length > 0 && (
            <Popover
              placement="bottomRight"
              trigger={["hover", "focus"]}
              content={
                <div style={{ display: "grid", gap: 8, maxWidth: 360 }}>
                  {notes.map((n) => (
                    <div
                      key={n.key}
                      // A badge and its text must stay paired (the price of collecting them into one button is "could one go missing or get mismatched"),
                      // and this attribute means that assertion does not have to guess at DOM depth
                      data-note={n.key}
                      style={{ display: "flex", alignItems: "flex-start", gap: 8 }}
                    >
                      <span style={{ flex: "0 0 auto", lineHeight: "22px" }}>{n.tag}</span>
                      <span
                        style={{
                          fontSize: 12,
                          lineHeight: 1.55,
                          color: "var(--csbi-ink-secondary)",
                          // The per-deployment state detail is multi-line text; the line breaks have to survive
                          whiteSpace: "pre-line",
                        }}
                      >
                        {n.detail}
                      </span>
                    </div>
                  ))}
                </div>
              }
            >
              <Button
                type="text"
                size="small"
                aria-label={`Notes (${notes.length})`}
                icon={
                  <Info
                    size={14}
                    // The button carries colour of its own when a "cannot be trusted" note exists — the notes are invisible by default, so colour is their only entry point
                    color={warned ? "var(--csbi-status-warning-ink)" : undefined}
                  />
                }
              />
            </Popover>
          )}
          {onRefresh && (
            <Tooltip title="Refresh">
              <Button
                type="text"
                size="small"
                aria-label="Refresh"
                icon={<RefreshCw size={14} />}
                loading={refreshing}
                onClick={onRefresh}
              />
            </Tooltip>
          )}
          {tableView && (
            <Tooltip title={asTable ? "Back to chart" : "Table view"}>
              <Button
                type={asTable ? "link" : "text"}
                size="small"
                aria-label="Table view"
                aria-pressed={asTable}
                icon={<Table2 size={14} />}
                onClick={() => setAsTable((v) => !v)}
              />
            </Tooltip>
          )}
          {(onExportCsv ?? onExportPng) && (
            <Dropdown
              menu={{
                items: [
                  ...(onExportCsv ? [{ key: "csv", label: "Export CSV", onClick: onExportCsv }] : []),
                  ...(onExportPng ? [{ key: "png", label: "Export PNG", onClick: onExportPng }] : []),
                ],
              }}
            >
              <Button type="text" size="small" aria-label="Export" icon={<Download size={14} />} />
            </Dropdown>
          )}
          {editActions && (
            <Dropdown menu={{ items: [] }} dropdownRender={() => <>{editActions}</>}>
              <Button type="text" size="small" aria-label="More" icon={<MoreHorizontal size={14} />} />
            </Dropdown>
          )}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, padding: asTable && tableView ? "0 4px" : 12 }}>
        {error ? (
          <div style={{ fontSize: 12, color: "var(--csbi-status-critical-ink)" }}>{error}</div>
        ) : loading && !result ? (
          // Placeholder text on first load only; a refetch keeps the old chart and dims it (the picture never flashes)
          <div style={{ fontSize: 12, color: "var(--csbi-ink-muted)" }}>Loading…</div>
        ) : asTable && tableView ? (
          tableView
        ) : showPlaceholder ? (
          <TileStatePlaceholder result={result!} />
        ) : (
          children
        )}
      </div>
    </Card>
  )
}
